export const USER_MANAGEMENT_TEXT = {
  USERS_TITLE: "User Access Management",
  ACCESS_GROUPS_TITLE: "Access Groups",
  CREATE_GROUP_TITLE: "Create Access Group",
  EDIT_GROUP_TITLE: "Edit Access Group",
  EDIT_USER_TITLE: "Edit User Role",
  LOAD_USERS_ERROR: "Unable to load users.",
  LOAD_GROUPS_ERROR: "Unable to load access groups.",
  LOAD_PERMISSIONS_ERROR: "Unable to load role permissions.",
  UPDATE_USER_SUCCESS: "User access groups updated successfully.",
  UPDATE_USER_ERROR: "Unable to update the user access groups.",
  CREATE_GROUP_SUCCESS: "Access group created successfully.",
  CREATE_GROUP_ERROR: "Unable to create the access group.",
  UPDATE_GROUP_SUCCESS: "Access group updated successfully.",
  UPDATE_GROUP_ERROR: "Unable to update the access group.",
  NO_USERS: "No users found.",
  NO_GROUPS: "No access groups found.",
  NO_AUDIT_LOGS: "No audit records found.",
  LOAD_AUDIT_ERROR: "Unable to load the audit trail.",
  AUDIT_FILTER_ALL: "All",
  USER_ROLE_FILTER_ALL: "All Roles",
  ACCESS_LEVEL_FILTER_ALL: "All Access Levels",
  ACCESS_LEVEL_FILTER_LABEL: "All Access Levels",
  ACCESS_STATUS_FILTER_ALL: "All Access Statuses",
  RESET_FILTERS: "Reset",
};

export const USER_ACCESS_STATUS_OPTIONS = ["Configure", "Configured"];
export const USER_MANAGEMENT_PAGE_SIZE = 10;
export const RBAC_EXPORT_POLL_INTERVAL_MS = 3000;
export const RBAC_EXPORT_MAX_POLLS = 40;

export const getPaginatedSerialNumber = (page, pageSize, index) =>
  (page - 1) * pageSize + index + 1;

const unwrapList = (response) => {
  const payload = response?.data ?? response ?? {};
  const candidates = [
    payload.results,
    payload.data?.results,
    payload.data?.data,
    payload.data,
    payload.users,
    payload.groups,
    payload.modules,
    payload,
  ];
  return candidates.find(Array.isArray) || [];
};

const toArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];
const valueOf = (object, keys, fallback = "") => {
  const key = keys.find((candidate) => object?.[candidate] !== undefined);
  return key ? object[key] : fallback;
};

const normalizeUserAccess = (accessLevel) =>
  toArray(accessLevel)
    .map((module) => {
      if (typeof module === "string" || typeof module === "number") {
        return String(module);
      }

      const moduleName = valueOf(
        module,
        ["module_name", "feature_name", "name", "label"],
        "",
      );
      const featureNames = toArray(
        valueOf(module, ["features", "permissions", "items"], []),
      )
        .sort(
          (first, second) =>
            Number(first?.display_order || 0) -
            Number(second?.display_order || 0),
        )
        .map((feature) =>
          valueOf(feature, ["feature_name", "name", "label"], ""),
        )
        .filter(Boolean);

      return moduleName && featureNames.length
        ? `${moduleName} (${featureNames.join(", ")})`
        : moduleName;
    })
    .filter(Boolean);

export const normalizeUserRoleFilters = (response) => {
  const payload = response?.data ?? response ?? {};
  const roleSources = [
    payload?.roles,
    payload?.filters?.roles,
    payload?.filter_options?.roles,
    payload?.role_filters,
    payload?.available_roles,
    payload?.data?.roles,
    payload?.data?.filters?.roles,
    payload?.data?.filter_options?.roles,
    payload?.data?.role_filters,
    payload?.data?.available_roles,
  ];
  const roles = roleSources.find(Array.isArray) || [];

  return [
    ...new Map(
      roles
        .map((role) => ({
          id: Number(valueOf(role, ["group_id", "id", "value"], Number.NaN)),
          name: valueOf(
            role,
            ["group_name", "role_name", "name", "label"],
            "",
          ),
        }))
        .filter((role) => Number.isFinite(role.id) && role.name)
        .map((role) => [role.id, role]),
    ).values(),
  ];
};

export const normalizeUserAccessLevelFilters = (response) => {
  const payload = response?.data ?? response ?? {};
  const accessLevelSources = [
    payload?.access_levels,
    payload?.filters?.access_levels,
    payload?.filter_options?.access_levels,
    payload?.data?.access_levels,
    payload?.data?.filters?.access_levels,
    payload?.data?.filter_options?.access_levels,
  ];
  const accessLevels = accessLevelSources.find(Array.isArray) || [];

  return accessLevels
    .map((accessLevel) => ({
      id: Number(
        valueOf(accessLevel, ["module_id", "id", "value"], Number.NaN),
      ),
      name: valueOf(
        accessLevel,
        ["module_name", "name", "label"],
        "",
      ),
      displayOrder: Number(accessLevel?.display_order || 0),
    }))
    .filter((accessLevel) => Number.isFinite(accessLevel.id) && accessLevel.name)
    .sort((first, second) => first.displayOrder - second.displayOrder);
};

export const normalizeUsers = (response) =>
  unwrapList(response).map((user) => {
    const groups = toArray(valueOf(user, ["groups", "user_groups", "roles"]));
    const roles = groups
      .map((group) =>
        typeof group === "string" || typeof group === "number"
          ? String(group)
          : valueOf(group, ["group_name", "name"], ""),
      )
      .filter(Boolean);
    const accessLevel = valueOf(
      user,
      ["access", "access_level", "modules", "features", "permissions"],
      [],
    );
    return {
      id: valueOf(user, [
        "id",
        "user_id",
        "user_details_id",
        "user_detail_id",
        "userid",
        "pk",
      ]),
      name: valueOf(user, ["name", "full_name", "username", "user_name"], "-"),
      email: valueOf(user, ["email", "email_address"], "-"),
      groups,
      groupIds: groups
        .map((group) => Number(valueOf(group, ["id", "group_id", "pk"], group)))
        .filter(Number.isFinite),
      roles,
      role: roles.join(", ") || "-",
      access: normalizeUserAccess(accessLevel),
      status: valueOf(
        user,
        ["status"],
        user.is_active === false ? "Inactive" : "Active",
      ),
      accessStatus: valueOf(user, ["status_type"], "-"),
      isActive: valueOf(user, ["is_active", "active"], true),
      lastLogin: valueOf(
        user,
        ["last_login_at", "last_login", "lastLogin"],
        "-",
      ),
    };
  });

export const normalizeGroups = (response) =>
  unwrapList(response).map((group) => {
    const permissionSources = [
      "modules",
      "module_permissions",
      "access",
      "permissions",
      "permissions_summary",
      "role_permissions",
    ].flatMap((key) => toArray(group?.[key]));
    const modules = [
      ...new Map(
        permissionSources
          .filter(
            (item) =>
              item &&
              typeof item === "object" &&
              (item.module_id !== undefined || item.module_name) &&
              Array.isArray(
                valueOf(item, [
                  "features",
                  "permissions",
                  "feature_ids",
                  "feature_details",
                ]),
              ),
          )
          .map((module, index) => [
            valueOf(module, ["module_id", "id", "module_name"], index),
            module,
          ]),
      ).values(),
    ].sort(
      (first, second) =>
        Number(first.display_order || 0) - Number(second.display_order || 0),
    );
    const directFeatures = toArray(
      valueOf(group, [
        "features",
        "feature_ids",
        "assigned_features",
      ]),
    );
    const moduleFeatures = modules.flatMap((module) =>
      toArray(
        valueOf(module, [
          "features",
          "permissions",
          "feature_ids",
          "feature_details",
        ]),
      ),
    );
    const features = [...directFeatures, ...moduleFeatures];
    const moduleSummaries = modules.map((module) => {
        const moduleName = valueOf(
          module,
          ["module_name", "name", "label"],
          "Module",
        );
        const featureNames = toArray(
          valueOf(module, [
            "features",
            "permissions",
            "feature_details",
          ]),
        )
          .sort(
            (first, second) =>
              Number(first?.display_order || 0) -
              Number(second?.display_order || 0),
          )
          .map((feature) =>
            valueOf(feature, ["feature_name", "name", "label"], feature),
          )
          .filter(Boolean);
        return featureNames.length
          ? `${moduleName} (${featureNames.join(", ")})`
          : moduleName;
      });
    const moduleSummary = moduleSummaries.join(", ");
    const suppliedSummary = valueOf(group, ["summary"], "");
    const flatFeatureSummary = directFeatures
      .map((feature) =>
        valueOf(feature, ["feature_name", "name", "label"], feature),
      )
      .filter(Boolean)
      .join(", ");
    return {
      id: valueOf(group, ["id", "group_id", "pk"]),
      name: valueOf(group, ["group_name", "name"], "-"),
      description: valueOf(group, ["description"], ""),
      summary:
        moduleSummary ||
        (typeof suppliedSummary === "string" ? suppliedSummary : "") ||
        flatFeatureSummary ||
        "-",
      summaryItems: moduleSummaries.length
        ? moduleSummaries
        : [
            (typeof suppliedSummary === "string" ? suppliedSummary : "") ||
              flatFeatureSummary ||
              "-",
          ],
      created: valueOf(group, ["created_at", "created_date"], "-"),
      updated: valueOf(group, ["updated_at", "updated_date"], "-"),
      featureIds: [
        ...new Set(
          features
            .map((feature) =>
              Number(
                valueOf(
                  feature,
                  ["id", "feature_id", "featureId", "pk"],
                  feature,
                ),
              ),
            )
            .filter(Number.isFinite),
        ),
      ],
      isActive: valueOf(group, ["is_active", "active"], true),
    };
  });

export const normalizePermissionGroups = (response) =>
  unwrapList(response).map((module) => ({
    id: valueOf(module, ["id", "module_id", "pk"]),
    label: valueOf(module, ["module_name", "name", "label"], "Module"),
    items: toArray(valueOf(module, ["features", "permissions", "actions"]))
      .map((feature) => ({
        id: Number(valueOf(feature, ["id", "feature_id", "pk"])),
        label: valueOf(
          feature,
          ["feature_name", "name", "label"],
          "Permission",
        ),
      }))
      .filter((feature) => Number.isFinite(feature.id)),
  }));

const normalizePermissionLabels = (permissions) => {
  if (permissions === null || permissions === undefined) return [];
  if (typeof permissions === "string" || typeof permissions === "number") {
    return [String(permissions)];
  }
  if (Array.isArray(permissions)) {
    return [
      ...new Set(
        permissions.flatMap((permission) =>
          normalizePermissionLabels(permission),
        ),
      ),
    ];
  }

  const ownLabel = valueOf(
    permissions,
    [
      "feature_name",
      "permission_name",
      "group_name",
      "module_name",
      "name",
      "label",
    ],
    "",
  );
  const nestedLabels = ["groups", "access", "features", "permissions", "items"]
    .flatMap((key) => normalizePermissionLabels(permissions[key]));

  return [...new Set([ownLabel, ...nestedLabels].filter(Boolean))];
};

const normalizeAuditPermissionGroups = (permissionData) => {
  if (permissionData === null || permissionData === undefined) return [];

  if (Array.isArray(permissionData)) {
    const groups = permissionData.flatMap(normalizeAuditPermissionGroups);
    return groups.reduce((result, group) => {
      const existingGroup = result.find(
        (item) => item.moduleName === group.moduleName,
      );
      if (existingGroup) {
        existingGroup.permissions = [
          ...new Set([...existingGroup.permissions, ...group.permissions]),
        ];
        return result;
      }
      return [...result, group];
    }, []);
  }

  if (typeof permissionData === "string" || typeof permissionData === "number") {
    return [{ moduleName: "Permissions", permissions: [String(permissionData)] }];
  }

  const moduleName = valueOf(
    permissionData,
    ["module_name", "group_name", "module", "group"],
    "",
  );
  const nestedPermissions = valueOf(
    permissionData,
    ["features", "permissions", "items", "actions"],
    undefined,
  );

  if (moduleName && nestedPermissions !== undefined) {
    return [{
      moduleName: String(moduleName),
      permissions: normalizePermissionLabels(nestedPermissions).filter(
        (permission) => permission !== moduleName,
      ),
    }];
  }

  if (moduleName) return [];

  const wrapperGroups = ["groups", "access", "module_permissions"]
    .flatMap((key) => normalizeAuditPermissionGroups(permissionData[key]));
  if (wrapperGroups.length) return wrapperGroups;

  const mappedGroups = Object.entries(permissionData)
    .filter(([, value]) => Array.isArray(value) || (value && typeof value === "object"))
    .flatMap(([key, value]) => {
      const groups = normalizeAuditPermissionGroups(value);
      if (!groups.length) return [];
      return groups.map((group) => ({
        moduleName: group.moduleName === "Permissions" ? key : group.moduleName,
        permissions: group.permissions,
      }));
    });
  if (mappedGroups.length) return mappedGroups;

  const labels = normalizePermissionLabels(permissionData);
  return labels.length ? [{ moduleName: "Permissions", permissions: labels }] : [];
};

export const normalizeAuditTrail = (response) =>
  unwrapList(response).map((record, index) => {
    const updatedOnValue = valueOf(
      record,
      [
        "performed_at",
        "updated_on",
        "timestamp",
        "created_at",
        "created_on",
        "date",
      ],
      "",
    );

    const previousPermissionData = valueOf(record, [
      "old_value",
      "previous_permissions",
      "old_permissions",
      "previous_value",
    ]);
    const currentPermissionData = valueOf(record, [
      "new_value",
      "current_permissions",
      "new_permissions",
      "current_value",
    ]);
    const previousPermissionGroups = normalizeAuditPermissionGroups(
      previousPermissionData,
    ).filter((group) => group.permissions.length);
    const currentPermissionGroups = normalizeAuditPermissionGroups(
      currentPermissionData,
    ).filter((group) => group.permissions.length);

    return {
    id: valueOf(record, ["id", "audit_id", "pk"], index),
    activity: valueOf(record, ["activity", "action", "event"], "-"),
    name: valueOf(
      record,
      [
        "entity_name",
        "name",
        "target_name",
        "group_name",
        "username",
        "user_name",
      ],
      "-",
    ),
    previousPermissions: previousPermissionGroups.flatMap(
      (group) => group.permissions,
    ),
    previousPermissionGroups,
    currentPermissions: currentPermissionGroups.flatMap(
      (group) => group.permissions,
    ),
    currentPermissionGroups,
    updatedBy: valueOf(
      record,
      [
        "performed_by_username",
        "updated_by",
        "performed_by",
        "created_by",
        "user",
      ],
      "-",
    ),
    updatedOn: updatedOnValue || "-",
    updatedOnValue,
    ipAddress: valueOf(record, ["ip_address", "ip", "user_ip"], "-"),
    };
  });

export const normalizeAuditActivityFilters = (response) => {
  const payload = response?.data ?? response ?? {};
  const activitySources = [
    payload.activities,
    payload.activity_list,
    payload.filters?.activities,
    payload.filter_options?.activities,
    payload.data?.activities,
    payload.data?.activity_list,
    payload.data?.filters?.activities,
    payload.data?.filter_options?.activities,
  ];
  const activities = activitySources.find(Array.isArray) || [];

  return [
    ...new Set(
      activities
        .map((item) =>
          typeof item === "string" || typeof item === "number"
            ? String(item)
            : valueOf(item, ["activity", "name", "label", "value"], ""),
        )
        .map((item) => String(item).trim())
        .filter(Boolean),
    ),
  ];
};

export const downloadAuditCsv = (records) => {
  const headers = [
    "Activity",
    "Name",
    "Previous Permissions",
    "Current Permissions",
    "Updated By",
    "Updated On",
    "IP Address",
  ];
  const escapeCell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const rows = records.map((record) => [
    record.activity,
    record.name,
    record.previousPermissions.join(", "),
    record.currentPermissions.join(", "),
    record.updatedBy,
    record.updatedOn,
    record.ipAddress,
  ]);
  const csv = [headers, ...rows].map((row) => row.map(escapeCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "user-management-audit-trail.csv";
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadApiFile = (response, fallbackFileName) => {
  const disposition = response?.headers?.["content-disposition"] || "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quotedName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const fileName = encodedName
    ? decodeURIComponent(encodedName)
    : quotedName || fallbackFileName;
  const blob =
    response?.data instanceof Blob
      ? response.data
      : new Blob([response?.data], {
          type: response?.headers?.["content-type"] || "application/octet-stream",
        });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const getRbacExportTaskId = (response) => {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};
  return payload.task_id ?? payload.taskId ?? null;
};

export const normalizeRbacExportStatus = (response) => {
  const responsePayload = response?.data ?? response ?? {};
  const payload = responsePayload?.data ?? responsePayload;

  return {
    status: String(
      payload?.task_status ?? payload?.status ?? responsePayload?.task_status ?? "",
    ).toUpperCase(),
    downloadUrl:
      payload?.download_url ??
      payload?.file_url ??
      responsePayload?.download_url ??
      responsePayload?.file_url ??
      "",
    message: payload?.message ?? responsePayload?.message ?? "",
  };
};

export const downloadFileFromUrl = (downloadUrl, fallbackFileName) => {
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fallbackFileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

export const getPaginationCount = (response, pageSize) => {
  const payload = response?.data ?? response ?? {};
  const total =
    payload.count ??
    payload.total ??
    payload.data?.count ??
    payload.data?.total ??
    unwrapList(response).length;
  return Math.max(Math.ceil(Number(total || 0) / pageSize), 1);
};
